import { Icon, IconButton } from '@elements';
import { Box } from '@mui/material';
import React, { useContext, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ErrorDialogContext } from '@contexts/ErrorDialogContext';
import { getErrorMessageFromAxiosError } from '@utils/getErrorMessageFromAxiosError';
import EmailIcon from '@images/email-icon.svg';
import EmailIconDisabled from '@images/email-icon-disabled.svg';
import DownloadIcon from '@images/download-icon.svg';
import DownloadIconDisabled from '@images/download-icon-disabled.svg';
import ReturnTicketsIcon from '@images/return-tickets.svg';
import ReturnTicketsIconDisabled from '@images/return-tickets-disabled.svg';
import useDownloadOrder from '@hooks/order/useDownloadOrder';
import useSendToEmailOrder from '@hooks/order/useSendToEmailOrder';
import useRefundTickets from '@hooks/ticket/useRefundTickets';
import Loader from '@components/loader/Loader';
import RefundTicketModal from '@components/modals/RefundTicketModal';
import RefundSuccessModal from '@components/modals/RefundSuccessModal';

type Props = {
  orderUuid: string;
  ticketUuids: string[];
};

const ActionsMobile = ({ orderUuid, ticketUuids }: Props) => {
  const queryClient = useQueryClient();
  const downloadOrder = useDownloadOrder(orderUuid);
  const sendToEmail = useSendToEmailOrder(orderUuid);
  const refundTickets = useRefundTickets(orderUuid);
  const [refundModalOpen, setRefundModalOpen] = React.useState(false);
  const [refundSuccessOpen, setRefundSuccessOpen] = React.useState(false);
  const isPending =
    sendToEmail.isPending || downloadOrder.isPending || refundTickets.isPending;
  const isReturnDisabled = refundTickets.isPending || ticketUuids.length === 0;
  const errorDialogContext = useContext(ErrorDialogContext);

  useEffect(() => {
    const error =
      downloadOrder.error || sendToEmail.error || refundTickets.error;
    if (error) {
      errorDialogContext.setErrorMessage(getErrorMessageFromAxiosError(error));
      errorDialogContext.setOpenErrorDialog(true);
    }
  }, [
    downloadOrder.error,
    sendToEmail.error,
    refundTickets.error,
    errorDialogContext,
  ]);

  return (
    <>
    <Box sx={{ display: 'flex', justifyContent: 'center', py: '9px' }}>
      <IconButton
        sx={{
          px: '21px',
          backgroundColor: '#FFFFFF',
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
        }}
        onClick={() => sendToEmail.mutate()}
        disabled={sendToEmail.isPending}
      >
        <Icon
          src={sendToEmail.isPending ? EmailIconDisabled : EmailIcon}
          width={28}
          height={28}
        />
      </IconButton>
      <IconButton
        sx={{ px: '21px', backgroundColor: '#FFFFFF', borderRadius: 0 }}
        onClick={() => downloadOrder.mutate()}
        disabled={downloadOrder.isPending}
      >
        <Icon
          src={downloadOrder.isPending ? DownloadIconDisabled : DownloadIcon}
          width={28}
          height={28}
        />
      </IconButton>
      <IconButton
        sx={{
          px: '21px',
          backgroundColor: '#FFFFFF',
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
        }}
        onClick={() => setRefundModalOpen(true)}
        disabled={isReturnDisabled}
      >
        <Icon
          src={isReturnDisabled ? ReturnTicketsIconDisabled : ReturnTicketsIcon}
          width={28}
          height={28}
        />
      </IconButton>
    </Box>
    <RefundTicketModal
      isOpen={refundModalOpen}
      closeHandler={() => setRefundModalOpen(false)}
      acceptHandler={() => {
        refundTickets.mutate(ticketUuids, {
          onSuccess: () => {
            setRefundModalOpen(false);
            setRefundSuccessOpen(true);
          },
          onError: () => setRefundModalOpen(false),
        });
      }}
      title="Вы хотите вернуть билеты?"
    />
    <RefundSuccessModal
      isOpen={refundSuccessOpen}
      closeHandler={() => {
        setRefundSuccessOpen(false);
        queryClient.invalidateQueries({ queryKey: ['orders'] });
      }}
    />
    <Loader isShowLoader={isPending} />
    </>
  );
};

export default ActionsMobile;
